import { Link, useLocation } from "@tanstack/react-router"
import type { ReactNode, RefObject } from "react"

import {
  isActivePath,
  isSiteNavGroup,
  type SiteNavItem,
  type SiteNavLink,
} from "@/components/nav-data"

import {
  menuClassNames,
  SiteNavMegaItemContent,
  SiteNavMenuTitle,
  siteNavLinkClassName,
} from "./SiteNavMenu"

interface SiteMobileMenuProps {
  items: SiteNavItem[]
  menuRef: RefObject<HTMLDivElement | null>
  onNavigate: () => void
}

// The desktop menus' items resized for touch: mega items 8px shorter around a
// 32px icon tile, plain items 4px taller.
const megaItemClassName = "h-14! gap-3! rounded-lg! px-3! py-2! font-sans tracking-[-0.01em]"
const plainItemClassName = "h-9! rounded-sm! px-3! py-0! font-sans font-medium tracking-[-0.01em]"

// The current page keeps the hover fill so it reads as where you are.
const linkClassName = (className: string, active: boolean) =>
  `${siteNavLinkClassName} ${className} ${menuClassNames.item.standard} ${active ? "bg-hover" : ""}`

function MenuLink({
  item,
  className,
  onNavigate,
  children,
}: {
  item: SiteNavLink
  className: string
  onNavigate: () => void
  children: ReactNode
}) {
  const location = useLocation()
  if (item.external) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClassName(className, false)}
      >
        {children}
      </a>
    )
  }
  const active = isActivePath(location.pathname, item.href)
  return (
    <Link
      to={item.href}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      className={linkClassName(className, active)}
    >
      {children}
    </Link>
  )
}

interface MenuSection {
  key: string
  title?: string
  items: SiteNavLink[]
  /** Icon tiles with descriptions, as in the desktop mega menu. */
  mega: boolean
}

/**
 * Each group's items under its title, then its secondary links,
 * and the top-level links last without a title.
 */
function getMenuSections(items: SiteNavItem[]): MenuSection[] {
  const groups = items.filter(isSiteNavGroup)
  const links = items.filter((item): item is SiteNavLink => !isSiteNavGroup(item))
  return [
    ...groups.flatMap((group) => [
      {
        key: group.label,
        title: group.menuLabel ?? group.label,
        items: group.items,
        mega: Boolean(group.menuLabel && group.promo),
      },
      ...(group.secondaryItems?.length
        ? [
            {
              key: `${group.label}-secondary`,
              items: group.secondaryItems,
              mega: false,
            },
          ]
        : []),
    ]),
    ...(links.length > 0 ? [{ key: "links", items: links, mega: false }] : []),
  ]
}

/**
 * Fullscreen mobile navigation. Renders below the (sticky, while open) header
 * with the desktop menus' items stacked in one column, each section under a
 * muted title. The featured post and the desktop menu's dividers are left out.
 */
export function SiteMobileMenu({ items, menuRef, onNavigate }: SiteMobileMenuProps) {
  return (
    <div
      id="site-mobile-menu"
      ref={menuRef}
      className="fixed inset-x-0 top-16 bottom-0 z-40 overflow-y-auto bg-(--surface-base) p-1 outline-none md:hidden"
      role="dialog"
      aria-label="Main menu"
      aria-modal="true"
      tabIndex={-1}
    >
      <nav className="flex flex-col gap-1">
        {getMenuSections(items).map((section, index) => (
          <section
            key={section.key}
            aria-label={section.title}
            className="site-menu-item flex flex-col"
            style={{ animationDelay: `${index * 60}ms` }}
          >
            {section.title ? <SiteNavMenuTitle>{section.title}</SiteNavMenuTitle> : null}
            {section.items.map((item) => (
              <MenuLink
                key={item.href}
                item={item}
                className={section.mega ? megaItemClassName : plainItemClassName}
                onNavigate={onNavigate}
              >
                {section.mega ? (
                  <SiteNavMegaItemContent item={item} iconTileClassName="size-8" />
                ) : (
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                )}
              </MenuLink>
            ))}
          </section>
        ))}
      </nav>
    </div>
  )
}
